import {
  FC,
  useState,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Tooltip } from 'antd';
import styles from './index.module.scss';
import { voiceVibes } from './constants';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

interface VoiceVibeCardProps {
  selectedNarrative: string;
  selectedVibe: string;
  setSelectedVibe: (vibe: string) => void;
  onSwiperInit?: (swiper: any) => void;
}
const VoiceVibeCard = forwardRef<any, VoiceVibeCardProps>(
  ({ selectedNarrative, selectedVibe, setSelectedVibe, onSwiperInit }, ref) => {
    const swiperRef = useRef<any>(null);
    useImperativeHandle(ref, () => ({
      slideNext: () => swiperRef.current?.slideNext(),
      slidePrev: () => swiperRef.current?.slidePrev(),
      slideTo: (idx: number) => swiperRef.current?.slideTo(idx),
    }));
    const [playingVibe, setPlayingVibe] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Cleanup audio when component unmounts
    useEffect(() => {
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }, []);

    const handlePlayAudio = useCallback(
      (vibe: string) => {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current = null;
        }

        const audioUrl = `http://media.tadatoon.com/media/speech/speech-preview-${selectedNarrative}-${vibe.toLowerCase()}.mp3`;
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        setPlayingVibe(vibe);

        // Handle audio end
        const handleEnded = () => {
          setPlayingVibe(null);
          audioRef.current = null;
        };

        audio.addEventListener('ended', handleEnded, { once: true });

        audio.play().catch((error) => {
          console.error('Error playing audio:', error);
          setPlayingVibe(null);
          audioRef.current = null;
        });
      },
      [selectedNarrative],
    );

    const handleStopAudio = useCallback(() => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingVibe(null);
    }, []);

    const handleVibeSelection = useCallback(
      (vibe: string) => {
        if (vibe === 'Auto (Based on Content)') {
          setSelectedVibe('Auto');
        } else {
          setSelectedVibe(vibe);
        }
      },
      [setSelectedVibe],
    );

    const handleAudioClick = useCallback(
      (e: React.MouseEvent, vibe: string) => {
        e.stopPropagation();

        if (playingVibe === vibe) {
          handleStopAudio();
        } else {
          handlePlayAudio(vibe);
        }
      },
      [playingVibe, handleStopAudio, handlePlayAudio],
    );

    return (
      <div className={styles.voice_vibe_container}>
        <Swiper
          spaceBetween={24}
          slidesPerView={'auto'}
          className={styles.voice_vibe_grid}
          onSwiper={(swiper: any) => {
            swiperRef.current = swiper;
            if (onSwiperInit) onSwiperInit(swiper);
          }}>
          {voiceVibes.map((voiceVibe, index) => {
            const isAutoVibe = voiceVibe.vibe === 'Auto (Based on Content)';
            const isSelected =
              selectedVibe === voiceVibe.vibe ||
              (isAutoVibe && selectedVibe === 'Auto');
            const isPlaying = playingVibe === voiceVibe.vibe;

            return (
              <SwiperSlide key={index} style={{ width: '167px' }}>
                <div
                  className={`${styles.voice_vibe_card} ${
                    isSelected ? styles.active : ''
                  }`}
                  onClick={() => handleVibeSelection(voiceVibe.vibe)}>
                  <div className={styles.card_content}>
                    {!isAutoVibe ? (
                      <div className={styles.image_container}>
                        <img
                          src={voiceVibe.image}
                          alt={voiceVibe.vibe}
                          aria-label={voiceVibe.vibe}
                        />
                      </div>
                    ) : (
                      <div className={styles.image_container}>
                        <img
                          src={voiceVibe.image}
                          alt={voiceVibe.vibe}
                          aria-label={voiceVibe.vibe}
                        />
                      </div>
                    )}

                    <div className={styles.voice_vibe}>
                      <Tooltip
                        title={voiceVibe.instructions}
                        placement="topRight"
                        className={styles.tooltip_overlay}>
                        <span className={styles.info_icon}>ⓘ</span>
                      </Tooltip>

                      <div className={styles.vibe_title}>
                        <h4>{voiceVibe.vibe}</h4>
                        {!isAutoVibe && (
                          <div className={styles.audio_controls}>
                            <button
                              type="button"
                              className={`${styles.play_button} ${
                                isPlaying ? styles.playing : ''
                              }`}
                              onClick={(e) =>
                                handleAudioClick(e, voiceVibe.vibe)
                              }
                              title={
                                isPlaying ? 'Stop preview' : 'Play preview'
                              }
                              aria-label={
                                isPlaying ? 'Stop preview' : 'Play preview'
                              }>
                              {isPlaying ? '⏹' : '▶'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  },
);

export default VoiceVibeCard;
